import { useState, useEffect } from 'react'
import { reservationApi } from '../services/api'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'

const HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]
const DURATION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8]

interface ReservationSlot {
  startAt: string
  endAt: string
  isOwn: boolean
}

export default function Reserve() {
  const [step, setStep] = useState(1)
  const [selectedDate, setSelectedDate] = useState('')
  const [reservedSlots, setReservedSlots] = useState<ReservationSlot[]>([])
  const [blockedHours, setBlockedHours] = useState<number[]>([])
  const [startHour, setStartHour] = useState<number | null>(null)
  const [duration, setDuration] = useState('')
  const [guestName, setGuestName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [isGuest, setIsGuest] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState<any>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  const ROOM_ID = process.env.REACT_APP_ROOM_ID || 'room-1'

  useEffect(() => {
    const token = localStorage.getItem('token')
    setIsLoggedIn(!!token)
  }, [])

  const fetchAvailability = async (date: string) => {
    setLoadingSlots(true)
    try {
      const response = await reservationApi.getAvailability(date)
      setReservedSlots(response.data.reservations)
      setBlockedHours(response.data.blockedHours || [])
    } catch {}
    finally { setLoadingSlots(false) }
  }

  const handleDateChange = (value: any) => {
    const date = value as Date
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateStr = `${year}-${month}-${day}`
    setSelectedDate(dateStr)
    setStartHour(null)
    setDuration('')
    fetchAvailability(dateStr)
  }

  const handleDateConfirm = () => {
    if (!selectedDate) return
    setError('')
    setStep(2)
  }

  const getHourStatus = (hour: number): 'available' | 'blocked' | 'own' | 'selected' | 'selectedRange' => {
    if (startHour !== null && duration) {
      const endHour = startHour + Number(duration)
      if (hour >= startHour && hour < endHour) return 'selectedRange'
    }
    if (startHour === hour) return 'selected'

    const slot = reservedSlots.find(s => {
      const start = new Date(s.startAt).getHours()
      const end = new Date(s.endAt).getHours()
      return hour >= start && hour < end
    })
    if (slot) return slot.isOwn ? 'own' : 'blocked'
    if (blockedHours.includes(hour)) {
      // ログイン中の場合、自分の予約の直前・直後は選択可能
      if (isLoggedIn) {
        const isAdjacentToOwn = reservedSlots.some(s => {
          if (!s.isOwn) return false
          const start = new Date(s.startAt).getHours()
          const end = new Date(s.endAt).getHours()
          return hour === end || hour === start - 1
        })
        if (isAdjacentToOwn) return 'available'
      }
      return 'blocked'
    }
    return 'available'
  }

  const isHourSelectable = (hour: number): boolean => {
    const status = getHourStatus(hour)
    return status === 'available'
  }

  const getAvailableDurations = (): number[] => {
    if (startHour === null) return []
    return DURATION_OPTIONS.filter(d => {
      const endHour = startHour + d
      if (endHour > 23) return false
      for (let h = startHour + 1; h < endHour; h++) {
        if (!isHourSelectable(h) && getHourStatus(h) !== 'selectedRange') return false
      }
      return true
    })
  }

  const endHour = startHour !== null && duration ? startHour + Number(duration) : null

  const formatHour = (h: number) => `${String(h).padStart(2, '0')}:00`

  const handleTimeNext = () => {
    if (startHour === null || !duration) {
      setError('開始時間と利用時間を選択してください')
      return
    }
    setError('')
    setStep(3)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!isGuest) {
      const token = localStorage.getItem('token')
      if (!token) {
        setError('会員としての予約にはログインが必要です')
        return
      }
    }

    setLoading(true)
    try {
      const startAt = `${selectedDate}T${formatHour(startHour!)}:00+09:00`
      const endAt = `${selectedDate}T${formatHour(endHour!)}:00+09:00`
      const data: any = { roomId: ROOM_ID, startAt, endAt }
      if (isGuest) {
        data.guestName = guestName
        data.guestEmail = guestEmail
      }
      const response = await reservationApi.create(data)
      setDone(response.data)
    } catch (err: any) {
      setError(err.response?.data?.error || '予約に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setStep(1)
    setSelectedDate('')
    setStartHour(null)
    setDuration('')
    setGuestName('')
    setGuestEmail('')
    setDone(null)
    setError('')
    setReservedSlots([])
    setBlockedHours([])
  }

  const tileDisabled = ({ date }: { date: Date }) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  if (done) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✅</div>
          <h1 style={styles.title}>予約完了</h1>
          <p style={styles.successText}>ご予約が確定しました！</p>
          <div style={styles.pinBox}>
            <p style={styles.pinLabel}>入室用PINコード</p>
            <p style={styles.pin}>{done.pin}</p>
            <p style={styles.pinNote}>※予約開始15分前から有効です</p>
            <p style={styles.pinNote}>※このPINコードは他の方と共有しないでください</p>
          </div>
          <p style={styles.mailNote}>確認メールをお送りしました。</p>
          <button onClick={handleReset} style={styles.primaryButton}>新しい予約をする</button>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>RentalRoom HQ</h1>

        {/* ステップインジケーター */}
        <div style={styles.stepIndicator}>
          {[
            { num: 1, label: '日付選択' },
            { num: 2, label: '時間選択' },
            { num: 3, label: 'お客様情報' }
          ].map(({ num, label }) => (
            <div key={num} style={styles.stepItem}>
              <div style={{
                ...styles.stepCircle,
                backgroundColor: step >= num ? '#1D9E75' : '#ddd',
                color: step >= num ? 'white' : '#999'
              }}>
                {step > num ? '✓' : num}
              </div>
              <span style={{
                ...styles.stepLabel,
                color: step >= num ? '#1D9E75' : '#999',
                fontWeight: step === num ? 'bold' : 'normal'
              }}>{label}</span>
              {num < 3 && (
                <div style={{
                  ...styles.stepLine,
                  backgroundColor: step > num ? '#1D9E75' : '#ddd'
                }} />
              )}
            </div>
          ))}
        </div>

        {error && <p style={styles.error}>{error}</p>}

        {/* STEP1: 日付選択 */}
        {step === 1 && (
          <div>
            <p style={styles.stepDescription}>📅 ご利用希望の日付を選んでください</p>
            <div style={styles.calendarCenter}>
              <Calendar
                onChange={handleDateChange}
                minDate={new Date()}
                tileDisabled={tileDisabled}
                locale="ja-JP"
              />
            </div>

            {selectedDate && (
              <div>
                {/* 予約状況サマリー */}
                <div style={styles.availabilityBox}>
                  <p style={styles.availabilityTitle}>📅 {selectedDate} の予約状況</p>
                  <div style={styles.legend}>
                    <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#EF4444' }}></span>他の方の予約</span>
                    <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#3B82F6' }}></span>自分の予約</span>
                  </div>
                  {loadingSlots ? (
                    <p style={styles.loadingText}>読み込み中...</p>
                  ) : reservedSlots.length === 0 ? (
                    <p style={styles.allAvailable}>✅ 終日空きあり</p>
                  ) : (
                    <div>
                      {reservedSlots.map((slot, idx) => {
                        const startH = new Date(slot.startAt).getHours()
                        const endH = new Date(slot.endAt).getHours()
                        return (
                          <div key={idx} style={{
                            ...styles.slotRow,
                            backgroundColor: slot.isOwn ? '#EEF4FF' : '#FFF0F0',
                            borderLeft: `4px solid ${slot.isOwn ? '#3B82F6' : '#EF4444'}`
                          }}>
                            <span>{slot.isOwn ? '🔵' : '🔴'}</span>
                            <span style={styles.slotTime}>
                              {formatHour(startH)} 〜 {formatHour(endH)}
                            </span>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: slot.isOwn ? '#3B82F6' : '#EF4444' }}>
                              {slot.isOwn ? 'あなたの予約' : '予約済み'}
                            </span>
                          </div>
                        )
                      })}
                      {!isLoggedIn && (
                        <div style={styles.loginHint}>
                          💡 <a href="/login" style={styles.loginLink}>ログイン</a>・
                          <a href="/register" style={styles.loginLink}>会員登録</a>
                          すると自分の予約が一目でわかります
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button onClick={handleDateConfirm} style={styles.primaryButton}>
                  {selectedDate} で予約する →
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP2: 時間選択 */}
        {step === 2 && (
          <div>
            <p style={styles.stepDescription}>🕐 利用する時間帯を選んでください</p>
            <div style={styles.selectedDateBadge}>
              📅 {selectedDate}
              <button onClick={() => setStep(1)} style={styles.changeButton}>変更</button>
            </div>

            {/* 凡例 */}
            <div style={styles.legend}>
              <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#1D9E75' }}></span>空き</span>
              <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#EF4444' }}></span>予約済み</span>
              <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#3B82F6' }}></span>自分の予約</span>
              <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#94A3B8' }}></span>選択不可</span>
              <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#FCD34D' }}></span>選択中</span>
            </div>

            {/* 時間ブロック */}
            <div style={styles.hourGrid}>
              {HOURS.map(hour => {
                const status = getHourStatus(hour)
                const selectable = isHourSelectable(hour)
                const isSelected = startHour === hour
                const isInRange = startHour !== null && duration && hour > startHour && hour < (startHour + Number(duration))

                let bgColor = '#E8F5F0'
                let textColor = '#1D6B3A'
                let cursor = 'pointer'
                let borderColor = '#1D9E75'

                if (status === 'blocked') {
                  bgColor = '#FEE2E2'; textColor = '#EF4444'; cursor = 'not-allowed'; borderColor = '#EF4444'
                } else if (status === 'own') {
                  bgColor = '#DBEAFE'; textColor = '#3B82F6'; cursor = 'not-allowed'; borderColor = '#3B82F6'
                } else if (isSelected) {
                  bgColor = '#FCD34D'; textColor = '#92400E'; borderColor = '#F59E0B'
                } else if (isInRange) {
                  bgColor = '#FEF3C7'; textColor = '#92400E'; borderColor = '#F59E0B'
                } else if (!selectable) {
                  bgColor = '#F1F5F9'; textColor = '#94A3B8'; cursor = 'not-allowed'; borderColor = '#CBD5E1'
                }

                return (
                  <div
                    key={hour}
                    onClick={() => {
                      if (!selectable) return
                      setStartHour(hour)
                      setDuration('')
                    }}
                    style={{
                      ...styles.hourBlock,
                      backgroundColor: bgColor,
                      color: textColor,
                      cursor,
                      border: `2px solid ${borderColor}`,
                      fontWeight: isSelected ? 'bold' : 'normal'
                    }}
                  >
                    <span style={styles.hourText}>{formatHour(hour)}</span>
                    {status === 'blocked' && <span style={styles.hourStatus}>×</span>}
                    {status === 'own' && <span style={styles.hourStatus}>自分</span>}
                    {isSelected && <span style={styles.hourStatus}>開始</span>}
                  </div>
                )
              })}
            </div>

            {startHour !== null && (
              <div style={styles.durationSection}>
                <label style={styles.label}>利用時間を選んでください</label>
                <div style={styles.durationGrid}>
                  {DURATION_OPTIONS.map(d => {
                    const available = getAvailableDurations().includes(d)
                    const selected = duration === String(d)
                    return (
                      <button
                        key={d}
                        onClick={() => available && setDuration(String(d))}
                        style={{
                          ...styles.durationButton,
                          backgroundColor: selected ? '#1D9E75' : available ? '#E8F5F0' : '#F1F5F9',
                          color: selected ? 'white' : available ? '#1D6B3A' : '#94A3B8',
                          cursor: available ? 'pointer' : 'not-allowed',
                          border: `2px solid ${selected ? '#1D9E75' : available ? '#1D9E75' : '#CBD5E1'}`
                        }}
                      >
                        {d}時間
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {startHour !== null && duration && endHour && (
              <div style={styles.timeConfirm}>
                🕐 {formatHour(startHour)} 〜 {formatHour(endHour)}（{duration}時間）
              </div>
            )}

            {!isLoggedIn && (
              <div style={styles.memberBenefit}>
                💡 <strong>会員登録のメリット</strong>：予約履歴の確認・管理が簡単に！
                <div style={{ marginTop: '6px' }}>
                  <a href="/register" style={styles.benefitLink}>無料で会員登録する</a>
                  <a href="/login" style={{ ...styles.benefitLink, marginLeft: '12px' }}>ログイン</a>
                </div>
              </div>
            )}

            <button
              onClick={handleTimeNext}
              style={{
                ...styles.primaryButton,
                opacity: startHour !== null && duration ? 1 : 0.5
              }}
              disabled={startHour === null || !duration}
            >
              次へ →
            </button>
          </div>
        )}

        {/* STEP3: お客様情報 */}
        {step === 3 && (
          <div>
            <p style={styles.stepDescription}>👤 お客様情報を入力してください</p>
            <div style={styles.confirmBox}>
              <div style={styles.confirmItem}>
                <span style={styles.confirmLabel}>📅 日付</span>
                <span>{selectedDate}</span>
                <button onClick={() => setStep(1)} style={styles.changeButton}>変更</button>
              </div>
              <div style={styles.confirmItem}>
                <span style={styles.confirmLabel}>🕐 時間</span>
                <span>{formatHour(startHour!)} 〜 {formatHour(endHour!)}（{duration}時間）</span>
                <button onClick={() => setStep(2)} style={styles.changeButton}>変更</button>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label}>予約種別</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input type="radio" checked={isGuest} onChange={() => setIsGuest(true)} />
                    <span>ゲスト（会員登録不要）</span>
                  </label>
                  <label style={styles.radioLabel}>
                    <input type="radio" checked={!isGuest} onChange={() => setIsGuest(false)} />
                    <span>会員</span>
                  </label>
                </div>
              </div>

              {isGuest ? (
                <>
                  <div style={styles.field}>
                    <label style={styles.label}>お名前 <span style={styles.required}>必須</span></label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      style={styles.input}
                      placeholder="山田 太郎"
                      required
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>メールアドレス <span style={styles.required}>必須</span></label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      style={styles.input}
                      placeholder="example@email.com"
                      required
                    />
                    <p style={styles.fieldNote}>※PINコードをメールでお送りします</p>
                  </div>
                  {!isLoggedIn && (
                    <div style={styles.memberBenefit}>
                      💡 <strong>会員登録</strong>すると予約履歴の確認・管理が便利になります
                      <div style={{ marginTop: '6px' }}>
                        <a href="/register" style={styles.benefitLink}>無料で会員登録する</a>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={styles.memberLoginBox}>
                  {isLoggedIn ? (
                    <p style={styles.loggedInText}>✅ ログイン中です。このまま予約を確定してください。</p>
                  ) : (
                    <div>
                      <p style={styles.fieldNote}>会員として予約するにはログインが必要です。</p>
                      <a href="/login" style={styles.primaryButtonLink}>ログインする</a>
                    </div>
                  )}
                </div>
              )}

              {(isGuest || isLoggedIn) && (
                <button type="submit" style={styles.primaryButton} disabled={loading}>
                  {loading ? '予約中...' : '予約を確定する'}
                </button>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f4f8', padding: '20px' },
  card: { backgroundColor: 'white', padding: '32px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', width: '100%', maxWidth: '580px' },
  title: { textAlign: 'center', marginBottom: '24px', color: '#1F4E79', fontSize: '24px' },
  stepIndicator: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '28px' },
  stepItem: { display: 'flex', alignItems: 'center' },
  stepCircle: { width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 },
  stepLabel: { fontSize: '12px', marginLeft: '6px', whiteSpace: 'nowrap' },
  stepLine: { width: '40px', height: '2px', margin: '0 8px' },
  stepDescription: { color: '#555', fontSize: '15px', marginBottom: '16px', textAlign: 'center' },
  calendarCenter: { display: 'flex', justifyContent: 'center', marginBottom: '16px' },
  availabilityBox: { backgroundColor: '#f8f9fa', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #eee' },
  availabilityTitle: { margin: '0 0 12px 0', color: '#333', fontSize: '15px', fontWeight: 'bold' },
  loadingText: { color: '#888', fontSize: '14px', textAlign: 'center' },
  allAvailable: { color: '#1D6B3A', fontWeight: 'bold', fontSize: '15px', textAlign: 'center' },
  slotRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', fontSize: '14px', marginBottom: '6px' },
  slotTime: { flex: 1, fontWeight: 'bold', color: '#333' },
  loginHint: { backgroundColor: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#92400E', marginTop: '8px' },
  loginLink: { color: '#1D9E75', fontWeight: 'bold', textDecoration: 'underline' },
  selectedDateBadge: { backgroundColor: '#f0f7ff', border: '1px solid #1D9E75', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', color: '#1D6B3A', fontWeight: 'bold', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px' },
  changeButton: { padding: '2px 10px', backgroundColor: 'transparent', border: '1px solid #1D9E75', borderRadius: '4px', color: '#1D9E75', cursor: 'pointer', fontSize: '12px', marginLeft: 'auto' },
  legend: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '12px' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '4px', color: '#555' },
  legendDot: { width: '12px', height: '12px', borderRadius: '3px', display: 'inline-block' },
  hourGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' },
  hourBlock: { padding: '10px 6px', borderRadius: '8px', textAlign: 'center', userSelect: 'none', transition: 'all 0.15s' },
  hourText: { display: 'block', fontSize: '14px', fontWeight: 'bold' },
  hourStatus: { display: 'block', fontSize: '10px', marginTop: '2px' },
  durationSection: { marginBottom: '16px' },
  durationGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' },
  durationButton: { padding: '10px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', textAlign: 'center' },
  timeConfirm: { backgroundColor: '#f0f7ee', border: '1px solid #1D9E75', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#1D6B3A', fontWeight: 'bold', fontSize: '15px', textAlign: 'center' },
  memberBenefit: { backgroundColor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#166534' },
  benefitLink: { color: '#1D9E75', fontWeight: 'bold', textDecoration: 'underline' },
  confirmBox: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '20px' },
  confirmItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 0', fontSize: '14px', color: '#333' },
  confirmLabel: { fontWeight: 'bold', minWidth: '60px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', color: '#555', fontSize: '14px', fontWeight: 'bold' },
  input: { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' },
  radioGroup: { display: 'flex', gap: '20px' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' },
  required: { color: '#e74c3c', fontSize: '12px', marginLeft: '4px' },
  fieldNote: { color: '#888', fontSize: '12px', marginTop: '4px' },
  memberLoginBox: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '16px', textAlign: 'center' },
  loggedInText: { color: '#1D6B3A', fontWeight: 'bold', fontSize: '14px' },
  primaryButton: { width: '100%', padding: '14px', backgroundColor: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold', marginTop: '8px' },
  primaryButtonLink: { display: 'block', width: '100%', padding: '12px', backgroundColor: '#1F4E79', color: 'white', borderRadius: '8px', fontSize: '14px', textDecoration: 'none', textAlign: 'center', marginTop: '8px', boxSizing: 'border-box' },
  error: { color: '#e74c3c', backgroundColor: '#fde8e8', padding: '10px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' },
  successIcon: { textAlign: 'center', fontSize: '48px', marginBottom: '16px' },
  successText: { color: '#1D6B3A', textAlign: 'center', fontSize: '16px', marginBottom: '24px' },
  pinBox: { backgroundColor: '#f0f7ee', border: '2px solid #1D9E75', borderRadius: '12px', padding: '24px', textAlign: 'center', margin: '20px 0' },
  pinLabel: { color: '#1D6B3A', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' },
  pin: { fontSize: '40px', fontWeight: 'bold', letterSpacing: '10px', color: '#1D6B3A', margin: '8px 0' },
  pinNote: { fontSize: '12px', color: '#888', marginTop: '4px' },
  mailNote: { textAlign: 'center', color: '#555', fontSize: '14px', marginBottom: '20px' },
}
